import type { IconName } from '../icons';
import { getLocale, type Locale } from '../i18n/i18n';

/**
 * Every word on the landing page, as typed data, in every language it ships in.
 *
 * omerta's landing — the structural model for this page — keeps its whole
 * document in one ~30KB template literal on a single line, which makes a copy
 * edit a merge hazard and leaves nothing testable. Steel of War separates its
 * content into a typed module with its own test. This follows Steel of War on
 * that point and omerta on the section layout.
 *
 * Claims here are checked against what the game actually does. A landing page
 * that promises a feature the build does not have is the one kind of copy that
 * costs more than it earns.
 *
 * TRANSLATION SHAPE. Marketing copy is prose, not interface labels, so it is
 * NOT keyed into `i18n/locales/*.json` — a `landing.faq.3.a` key buys nothing
 * and makes the copy unreadable to whoever has to edit it. Instead each locale
 * supplies a parallel block of the same shape, and the non-translatable parts
 * (icons, image paths, hrefs) are declared ONCE and zipped in by index, so a
 * new unit's artwork cannot drift between languages. `landing-content.test.ts`
 * asserts the blocks stay the same length.
 *
 * The exports are resolved at import time. That is correct because switching
 * language reloads the page (see landing-screen.ts's `set-locale` handler), so
 * a module-level pick can never be stale.
 */

export interface Step {
  title: string;
  body: string;
  icon: IconName;
}

export interface Pillar {
  title: string;
  body: string;
  icon: IconName;
}

export interface FaqItem {
  q: string;
  a: string;
}

export interface UnitShowcase {
  name: string;
  category: string;
  description: string;
  image: string;
}

export interface ProgressionImage {
  src: string;
  /** Horizontal centre on the plot, as a CSS length (the image is `translateX(-50%)`d). */
  x: string;
  /** Distance from the plot's bottom edge to this building's footing. */
  y: string;
  /** Rendered width as a share of the plot, which also sets apparent scale. */
  w: string;
}

export interface ProgressionStep {
  label: string;
  description: string;
  images: ProgressionImage[];
}

// ─── Shared, non-translatable data ──────────────────────────────────────────
// Declared once so artwork and routes cannot diverge between languages.

const STEP_ICONS: IconName[] = ['colony', 'regolith', 'research', 'battle'];
const PILLAR_ICONS: IconName[] = ['map', 'deltaV', 'wall', 'chat', 'crosshair'];
// Exported so a test can pin its length against EN.units/ES.units: this array and
// both locales' `units` blocks are zipped together BY INDEX (see UNITS
// below), so an edit to one that isn't mirrored in the other two silently
// mis-pairs every unit after the edit point with the wrong image instead of
// failing loudly.
//
// These point at `-landing` derivatives, NOT the plain `/units/<slug>.webp`
// in-game thumbnail: the in-game version is background-removed (transparent,
// composited with a team-colour tint via unit-art.ts), while the showcase
// here wants the full scenic render. Every entry is therefore an opaque,
// baked-background render (verified: `magick <f> -format %[opaque]` is True
// for all nine), which is why the bay fits them rather than floating them.
export const UNIT_IMAGES: string[] = [
  '/units/unit-vehicle-medium-landing.webp',
  '/units/unit-aircraft-gunship-landing.webp',
  '/units/unit-defence-bunker-landing.webp',
  '/units/unit-infantry-light-landing.webp',
  '/units/unit-defence-walker-landing.webp',
  '/units/unit-siege-artillery-landing.webp',
  '/units/unit-siege-mortar-landing.webp',
  '/units/unit-settler-convoy-landing.webp',
  '/units/unit-vehicle-light-landing.webp',
];

/**
 * Hero and closing-CTA art: absolutely-positioned composites with a
 * drop-shadow, so (unlike UNIT_IMAGES above) these need REAL transparent
 * cutouts, not the baked-background `-landing` renders — verified via each
 * file's WebP VP8X alpha flag.
 *
 * The closing-CTA vehicle is deliberately the same file as the hero's
 * mid-ground tank: both represent ATTACK_ROVER, the "Medium vehicle (IFV)"
 * (see `engine/unit-art.ts`), and it is the only IFV art this game has.
 */
export const HERO_ART = {
  fortress: '/buildings/command-t6-1k.webp',
  tank: '/units/unit-vehicle-medium.webp',
  gunship: '/units/unit-aircraft-gunship.webp',
};
export const FINAL_ART = '/units/unit-vehicle-medium.webp';

/**
 * The four colony stages, as buildings standing on the isometric plot.
 *
 * These are `-plot` derivatives, not the `-1k` colony-view renders they come
 * from. The originals pad their building onto a 1024-square canvas by wildly
 * different amounts — command-t1 occupies 431x137 of its frame with 110px of
 * empty canvas below it, while command-t6 is full-bleed — so positioning them
 * by a shared "bottom edge sits on the ground" rule puts some buildings on the
 * dirt and others floating above it. `magick -trim +repage` makes the claim
 * true for all six, which is the whole reason the derivatives exist; `y` is
 * then a real ground position rather than per-image padding compensation.
 *
 * `w` still differs per image, and has to: trimmed aspect ratios run from
 * 3.1:1 (the t1 slab) to about 1:1 (the t10 barracks), so equal widths would
 * not read as equal buildings. These are tuned by eye against the plot.
 */
const PROGRESSION_IMAGES: ProgressionImage[][] = [
  [{ src: '/buildings/command-t1-plot.webp', x: '50%', y: '30%', w: '46%' }],
  [
    { src: '/buildings/barracks-t1-plot.webp', x: '70%', y: '24%', w: '32%' },
    { src: '/buildings/command-t1-plot.webp', x: '38%', y: '30%', w: '46%' },
  ],
  [
    { src: '/buildings/barracks-t4-plot.webp', x: '72%', y: '22%', w: '36%' },
    { src: '/buildings/command-t3-plot.webp', x: '38%', y: '26%', w: '48%' },
  ],
  [
    { src: '/buildings/barracks-t10-plot.webp', x: '74%', y: '20%', w: '38%' },
    { src: '/buildings/command-t6-plot.webp', x: '40%', y: '22%', w: '56%' },
  ],
];
const FOOTER_HREFS = ['/login', '/register'];

/** The text half of each block, per locale. Shapes must match across locales. */
interface LocaleCopy {
  hero: { kicker: string; title: string; sub: string; primaryCta: string; secondaryCta: string };
  /** Sub-heading prose under the "How it works" / "Your arsenal" / "The rules" headings. */
  ledes: { how: string; arsenal: string; rules: string };
  steps: { title: string; body: string }[];
  pillars: { title: string; body: string }[];
  faq: FaqItem[];
  units: { name: string; category: string; description: string }[];
  progression: { label: string; description: string }[];
  footer: string[];
}

const EN: LocaleCopy = {
  hero: {
    kicker: 'Mars · Persistent worlds · Browser',
    title: 'Hold your ground on a planet that does not want you.',
    sub: 'Found a colony in the dust, raise it out of raw regolith, and defend it against every other commander who wants the same ridge. No install. No client. Just a tab.',
    primaryCta: 'Play free',
    secondaryCta: 'How it works',
  },
  ledes: {
    how: 'Four moves, one season. Everything you build is earned on the map, in real time, against real people.',
    arsenal: 'Nine units across six classes. Pick one to inspect it.',
    rules: 'A persistent world only works if everyone on it is playing the same game. These hold for every world, every season.',
  },
  steps: [
    { title: 'Plant a colony', body: 'Pick a world, claim a plot, and start with a Command Center and nothing else. Where you land decides who your neighbours are for the rest of the season.' },
    { title: 'Dig in', body: 'Regolith, water ice, helium-3 and composite all come out of the ground on their own schedule. Every structure you raise is capped by your Command Center, so there are no shortcuts past the foundation.' },
    { title: 'Climb the tech ladder', body: 'Research unlocks whole classes of unit, but each step demands real infrastructure first. You cannot read your way to an army you have no barracks to train.' },
    { title: 'Take the ridge', body: 'Scout first, commit second. Armies march in real time across the map, and what you send is what you might not get back.' },
  ],
  pillars: [
    { title: 'One world at a time', body: 'A commander plays a single world per season. No alt-farming your own empire from three windows — your neighbours are people, not your other accounts.' },
    { title: 'Money buys time, never power', body: 'Delta-V skips a timer you could have waited out, buys material you could have mined, or buys a title nobody has to care about. It has never won a fight.' },
    { title: 'Losses are real', body: 'Units that die are gone. Walls that fall get rebuilt out of your own stockpile. A bad raid costs you a week, which is what makes a good one worth something.' },
    { title: 'A world that talks back', body: 'Every world has its own chat. Alliances, warnings, gloating and the occasional genuinely useful tip — all of it scoped to the people who share your map.' },
    { title: 'Choose how you march', body: 'Every army goes out in a formation, and every formation is a trade. Forced March lands sooner but hits softer. Assault Wedge hits harder but arrives later and hauls less. Raider Column carries and loots more but marches heavy. Free to pick, never a purchase, and the choice is visible in the battle log afterwards.' },
  ],
  faq: [
    { q: 'Is it really free?', a: 'Yes. The whole game is playable without spending anything. Delta-V, the premium currency, only ever buys time, raw material or cosmetic titles — never combat advantage.' },
    { q: 'Do I need to install anything?', a: 'No. It runs in a browser tab on a phone or a desktop. There is no launcher, no download and no app store.' },
    { q: 'What happens while I am offline?', a: 'Your colony keeps producing and your timers keep running — resources accumulate against the clock rather than against your attention. You can be raided while you are away, which is what walls and a garrison are for.' },
    { q: 'How long is a season?', a: 'Worlds run for months, not minutes. This is a game you check on, not one you grind in a sitting.' },
    { q: 'What are formations?', a: 'When you send an army you also pick how it marches. Balanced has no bonuses and no costs. Forced March cuts the march time by a fifth but trims attack. Assault Wedge adds attack but slows the march and reduces what you can carry home. Raider Column carries more and lifts a bigger share of the stockpile, but is slower and weaker in the fight. Scouts on a pure recon run ignore formations. Formations are free; they are tactics, not something you buy.' },
    { q: 'Can I play with friends?', a: 'Join the same world and you share a map and a chat channel. Whether you then cooperate is between you.' },
    { q: 'What happens to my account if I stop?', a: 'A colony left untouched long enough is abandoned and its ground is released back to the map. You can close your account from Settings at any time.' },
  ],
  units: [
    { name: 'Medium Tank', category: 'Vehicle', description: 'The backbone of any assault column. Slow, heavy, and hard to argue with.' },
    { name: 'Gunship', category: 'Aircraft', description: 'Air superiority over the ridge. Nothing on the ground moves while one of these is overhead.' },
    { name: 'Sector Holder', category: 'Defence', description: 'Dug in, and that is the point. A fire team that owns one arc of the perimeter and makes anything crossing it pay.' },
    { name: 'Light Infantry', category: 'Defence', description: 'Cheap, fast to train, and the only unit that can actually hold a doorway.' },
    { name: 'Turret Walker', category: 'Defence', description: 'Mobile fire platform. Walks slow, shoots far, holds ground that vehicles cannot.' },
    { name: 'Siege Artillery', category: 'Siege', description: 'Long-range bombardment. Walls crack before the garrison sees who is firing.' },
    { name: 'Mortar Tank', category: 'Siege', description: 'Mid-range siege armor. Rolls up, lobs high-explosive, rolls on.' },
    { name: 'Settler Convoy', category: 'Support', description: 'Unarmed but essential. The only way to claim abandoned ground as your own.' },
    { name: 'Scout Bike', category: 'Recon', description: 'The fastest thing on the map. Sees everything, fights nothing.' },
  ],
  progression: [
    { label: 'Foundations', description: 'Bare regolith and a claim marker. Everything starts here.' },
    { label: 'Outpost', description: 'Walls go up, mines start running, the first scouts roll out.' },
    { label: 'Stronghold', description: 'A real base with layered defenses and a tech lab pushing boundaries.' },
    { label: 'Fortress', description: 'The map knows your name. Heavy armor, deep reserves, nothing gets through.' },
  ],
  footer: ['Play', 'Sign up'],
};

const ES: LocaleCopy = {
  hero: {
    kicker: 'Marte · Mundos persistentes · Navegador',
    title: 'Defiende tu terreno en un planeta que no te quiere.',
    sub: 'Funda una colonia en el polvo, levántala a partir de regolito en bruto y defiéndela de cualquier otro comandante que quiera la misma cresta. Sin instalación. Sin cliente. Solo una pestaña.',
    primaryCta: 'Jugar gratis',
    secondaryCta: 'Cómo funciona',
  },
  ledes: {
    how: 'Cuatro movimientos, una temporada. Todo lo que construyes se gana en el mapa, en tiempo real, contra gente real.',
    arsenal: 'Nueve unidades en seis clases. Elige una para inspeccionarla.',
    rules: 'Un mundo persistente solo funciona si todos juegan al mismo juego. Esto rige en todos los mundos, en todas las temporadas.',
  },
  steps: [
    { title: 'Funda una colonia', body: 'Elige un mundo, reclama una parcela y empieza con un Centro de Mando y nada más. El lugar donde aterrices decide quiénes serán tus vecinos durante el resto de la temporada.' },
    { title: 'Excava', body: 'El regolito, el hielo de agua, el helio-3 y el compuesto salen del suelo a su propio ritmo. Cada estructura que levantas está limitada por tu Centro de Mando, así que no hay atajos que salten los cimientos.' },
    { title: 'Sube por la escalera tecnológica', body: 'La investigación desbloquea clases enteras de unidades, pero cada paso exige infraestructura real antes. No puedes leer tu camino hasta un ejército que no tienes cuartel donde entrenar.' },
    { title: 'Toma la cresta', body: 'Explora primero, comprométete después. Los ejércitos marchan por el mapa en tiempo real, y lo que envías es lo que quizá no recuperes.' },
  ],
  pillars: [
    { title: 'Un mundo cada vez', body: 'Un comandante juega un único mundo por temporada. Nada de cultivar tu propio imperio desde tres ventanas: tus vecinos son personas, no tus otras cuentas.' },
    { title: 'El dinero compra tiempo, nunca poder', body: 'El Delta-V se salta un temporizador que podrías haber esperado, compra material que podrías haber extraído o compra un título que a nadie tiene por qué importarle. Jamás ha ganado un combate.' },
    { title: 'Las pérdidas son reales', body: 'Las unidades que mueren no vuelven. Los muros que caen se reconstruyen con tus propias reservas. Una incursión fallida te cuesta una semana, y eso es justo lo que hace que una buena valga algo.' },
    { title: 'Un mundo que responde', body: 'Cada mundo tiene su propio chat. Alianzas, advertencias, fanfarronadas y algún consejo verdaderamente útil, todo limitado a quienes comparten tu mapa.' },
    { title: 'Elige cómo marchas', body: 'Todo ejército sale en una formación, y toda formación es un intercambio. La marcha forzada llega antes pero golpea más flojo. La cuña de asalto golpea más fuerte pero llega más tarde y carga menos. La columna de saqueo carga y saquea más, pero marcha pesada. Es gratis elegir, nunca una compra, y la elección queda a la vista en el registro de batallas.' },
  ],
  faq: [
    { q: '¿Es gratis de verdad?', a: 'Sí. El juego entero se puede jugar sin gastar nada. El Delta-V, la moneda premium, solo compra tiempo, materia prima o títulos cosméticos; nunca ventaja en combate.' },
    { q: '¿Tengo que instalar algo?', a: 'No. Funciona en una pestaña del navegador, en el móvil o en el ordenador. No hay lanzador, ni descarga, ni tienda de aplicaciones.' },
    { q: '¿Qué pasa mientras estoy desconectado?', a: 'Tu colonia sigue produciendo y tus temporizadores siguen corriendo: los recursos se acumulan contra el reloj, no contra tu atención. Pueden saquearte mientras no estás, y para eso están los muros y la guarnición.' },
    { q: '¿Cuánto dura una temporada?', a: 'Los mundos duran meses, no minutos. Este es un juego que consultas, no uno que exprimes de una sentada.' },
    { q: '¿Qué son las formaciones?', a: 'Al enviar un ejército eliges también cómo marcha. Equilibrada no tiene bonificaciones ni costes. La marcha forzada reduce una quinta parte el tiempo de marcha, pero recorta el ataque. La cuña de asalto suma ataque, pero ralentiza la marcha y reduce lo que puedes traer de vuelta. La columna de saqueo carga más y se lleva una parte mayor de las reservas, pero es más lenta y más débil en el combate. Los exploradores en una misión de reconocimiento puro ignoran las formaciones. Son gratis: son táctica, no algo que se compre.' },
    { q: '¿Puedo jugar con amigos?', a: 'Entrad en el mismo mundo y compartiréis mapa y canal de chat. Que luego cooperéis o no es cosa vuestra.' },
    { q: '¿Qué pasa con mi cuenta si lo dejo?', a: 'Una colonia que se deja intacta el tiempo suficiente queda abandonada y su terreno vuelve al mapa. Puedes cerrar tu cuenta desde Ajustes cuando quieras.' },
  ],
  units: [
    { name: 'Tanque medio', category: 'Vehículo', description: 'La columna vertebral de cualquier asalto. Lento, pesado y difícil de rebatir.' },
    { name: 'Cañonera', category: 'Aeronave', description: 'Superioridad aérea sobre la cresta. Nada se mueve en tierra mientras una de estas sobrevuela.' },
    { name: 'Defensor de sector', category: 'Defensa', description: 'Atrincherado, y de eso se trata. Un equipo de fuego que domina un arco del perímetro y hace pagar a todo lo que lo cruza.' },
    { name: 'Infantería ligera', category: 'Defensa', description: 'Barata, rápida de entrenar y la única unidad capaz de mantener una puerta.' },
    { name: 'Caminante de torreta', category: 'Defensa', description: 'Plataforma de fuego móvil. Camina despacio, dispara lejos y mantiene terreno que los vehículos no pueden.' },
    { name: 'Artillería de asedio', category: 'Asedio', description: 'Bombardeo de largo alcance. Los muros se agrietan antes de que la guarnición vea quién dispara.' },
    { name: 'Tanque de mortero', category: 'Asedio', description: 'Blindado de asedio de medio alcance. Se acerca, lanza explosivos y sigue adelante.' },
    { name: 'Convoy de colonos', category: 'Apoyo', description: 'Desarmado pero imprescindible. La única forma de reclamar terreno abandonado como propio.' },
    { name: 'Moto de reconocimiento', category: 'Reconocimiento', description: 'Lo más rápido del mapa. Lo ve todo, no combate con nada.' },
  ],
  progression: [
    { label: 'Cimientos', description: 'Regolito desnudo y un marcador de reclamación. Todo empieza aquí.' },
    { label: 'Puesto avanzado', description: 'Se alzan los muros, arrancan las minas y salen los primeros exploradores.' },
    { label: 'Bastión', description: 'Una base de verdad, con defensas en capas y un laboratorio empujando los límites.' },
    { label: 'Fortaleza', description: 'El mapa conoce tu nombre. Blindaje pesado, reservas profundas, nada entra.' },
  ],
  footer: ['Jugar', 'Registrarse'],
};

const COPY: Record<Locale, LocaleCopy> = { en: EN, es: ES };

/** Exported for the test that asserts every locale ships the same shape. */
export const LOCALE_COPY = COPY;

const copy = COPY[getLocale()] ?? EN;

export const HERO = copy.hero;

export const LEDES = copy.ledes;

export const STEPS: Step[] = copy.steps.map((s, i) => ({ ...s, icon: STEP_ICONS[i] }));

export const PILLARS: Pillar[] = copy.pillars.map((p, i) => ({ ...p, icon: PILLAR_ICONS[i] }));

export const FAQ: FaqItem[] = copy.faq;

export const UNITS: UnitShowcase[] = copy.units.map((u, i) => ({ ...u, image: UNIT_IMAGES[i] }));

export const PROGRESSION: ProgressionStep[] = copy.progression.map((p, i) => ({
  ...p,
  images: PROGRESSION_IMAGES[i],
}));

export const FOOTER_LINKS: { label: string; href: string }[] = copy.footer.map((label, i) => ({
  label,
  href: FOOTER_HREFS[i],
}));
